import {AppBox, AppButton, AppContainer, AppModal, AppPaginator, AppTitle} from "../../../commons/components";
import {useEffect, useState} from "react";
import {useAppDispatch, useAppSelector} from "../../../hooks/app";
import RefereeList from "./components/RefereeList";
import {
    cleanUpRefereeList,
    fetchManageRefereeList,
} from "../../../redux/slices/football/admin/referee/manage-referee-list-slice";
import {StatusEnum} from "../../../commons/enums/status-enum";
import IconWhistle from "../../../commons/components/icons/whistle";
import RefereeAddEdit from "./components/refereeAddEdit/RefereeAddEdit";
import RefereeDelete from "./components/RefereeDelete";
import RefereeDeleteInprogress from "./components/RefereeDeleteInprogress";
import {ManageFootballReferee} from "../../../commons/models/football/admin/manage-football-referee";
import IconBin from "../../../commons/components/icons/bin";
import {refereeDelete} from "../../../redux/slices/football/admin/referee/manage-referee-delete-slice";
import {setPageTitle} from "../../../redux/slices/page-title-slice";
import {useSnackbar} from "../../../redux/snackbarProvider";
import setDocumentTitle from "../../../commons/utilities/document-title";
import {AxiosError} from "axios";
import {IServerErrorResponse} from "../../../commons/components/interface";
import {cleanUpStepperFormData} from "../../../redux/slices/stepper-form-slice";
import IconEdit from "../../../commons/components/icons/edit";
import {Can} from "../../../casl/Can";
import {RolePolicyActionEnum} from "../../../commons/enums/role-policy-action.enum";
import usePagination from "../../../hooks/usePagination";
import FilterToolbar from "../../../commons/components/ui-components/FilterToolbar";
import useFilter from "../../../hooks/useFilter";

export default function RefereeManage() {
    const [isRefereeAddEdit, setRefereeAddEdit] = useState(false);
    const [isRefereeDelete, setRefereeDelete] = useState(false);
    const [selectedReferee, setSelectedReferee] = useState<ManageFootballReferee | null>(null);
    const dispatch = useAppDispatch();
    const addSnackbar = useSnackbar();
    const {refereeList} = useAppSelector((state) => state.footballManageRefereeList);
    const {refereeDeleteStatus} = useAppSelector((state) => state.footballManageRefereeDelete);
    const {page, perPage, pageChange, pageLimitChange} = usePagination();
    const {searchText, sortOrder, sortBy} = useFilter();
    const { refereeAddResponseStatus } = useAppSelector((state) => state.footballManageRefereeAdd);
    const { refereeEditResponseStatus } = useAppSelector((state) => state.footballManageRefereeEdit);

    const fetchReferees =()=>{
        dispatch(fetchManageRefereeList({sort: sortBy, sortOrder, name: searchText, page,perPage}));
    }
    useEffect(() => {
        dispatch(setPageTitle("Referees"));
        return () => {
            dispatch(cleanUpRefereeList());
        };
    }, []);

    useEffect(() => {
       fetchReferees();
    }, [page, perPage, sortBy, sortOrder, searchText]);

    const handleDeleteConfirm = (e: Event) => {
        e.preventDefault();
        if (selectedReferee) {
            setRefereeDelete(false);
            dispatch(refereeDelete(selectedReferee.id as number))
                .unwrap()
                .then(() => {
                    addSnackbar({
                        key: "referee-delete-success",
                        text: "Referee Deleted Successfully",
                        variant: "success",
                    });
                    fetchReferees();
                })
                .catch((error: AxiosError<IServerErrorResponse>) => {
                    addSnackbar({
                        key: "error",
                        text: error.response?.data.message,
                        variant: "danger",
                    });
                });
        }
    };

    const handleAfterAddEditClose = () => {
        setRefereeAddEdit(false);
        setSelectedReferee(null);
        setDocumentTitle(["Referees", "Manage"]);
        dispatch(cleanUpStepperFormData());
    };

    return (
        <>
            <AppContainer>
                <AppBox py="md" flexDirection="column" gap="md">
                    <AppBox flexDirection="column" gap="xs">
                        <AppBox alignItems="center" justifyContent="space-between">
                            <AppTitle as="h4">All Referees</AppTitle>
                            <Can I={RolePolicyActionEnum.CREATE} a={ManageFootballReferee}>
                                {refereeList.length > 0 &&
                                    <AppButton label="Add Referee" onClick={() => setRefereeAddEdit(true)}/>}
                            </Can>
                        </AppBox>
                        <FilterToolbar enableStatus={false}/>
                        <RefereeList
                            onAddReferee={() => setRefereeAddEdit(true)}
                            onEditReferee={(referee) => {
                                setSelectedReferee(referee);
                                setRefereeAddEdit(true);
                            }}
                            onDeleteReferee={(referee) => {
                                setSelectedReferee(referee);
                                setRefereeDelete(true);
                            }}
                        />
                    </AppBox>
                    {refereeList?.pageCount > 1 &&
                        <AppPaginator
                            perPage={refereeList.perPage}
                            pageCount={refereeList.pageCount}
                            currentPage={refereeList.page}
                            pageChange={pageChange}
                            perPageLimitChange={pageLimitChange}/>
                    }
                </AppBox>
            </AppContainer>
            <AppModal
                title={selectedReferee ? "Edit Referee Information" : "Add New Referee"}
                titleIcon={selectedReferee ? <IconEdit/> : <IconWhistle/>}
                opened={isRefereeAddEdit}
                modalSize="lg"
                onClose={handleAfterAddEditClose}
                withLoadingButton={
                    refereeAddResponseStatus === StatusEnum.Loading || refereeEditResponseStatus === StatusEnum.Loading
                }
                withoutButtons
            >
                <RefereeAddEdit
                    refereeData={selectedReferee}
                    onCancel={handleAfterAddEditClose}
                    onSave={() => {
                        fetchReferees();
                        setRefereeAddEdit(false);
                        setSelectedReferee(null);
                    }}
                />
            </AppModal>
            <AppModal
                opened={isRefereeDelete}
                onClose={(e) => {
                    setRefereeDelete(false);
                }}
                title="Want To Delete This Referee?"
                titleIcon={<IconBin/>}
                iconColor="danger"
                confirmButtonLabel="Delete"
                onSave={(e) => {
                    handleDeleteConfirm(e as Event);
                }}
            >
                <RefereeDelete referee={selectedReferee as ManageFootballReferee}/>
            </AppModal>
            <AppModal
                opened={refereeDeleteStatus === StatusEnum.Loading}
                title="Deleting Referee"
                titleIcon={<IconBin/>}
                withoutCancelButton
                withoutButtons
                withoutCloseButton
            >
                <RefereeDeleteInprogress referee={selectedReferee as ManageFootballReferee}/>
            </AppModal>
        </>
    );
}
